import * as Sentry from '@sentry/react';
import { useFeatureFlag } from 'features/feature-set/useFeatureFlag';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useContentBoard } from 'features/content-whiteboard/hooks/useContentBoard';
import { selectIsEmbedded } from 'features/sdk/sdkStateSlice';
import { SignalingSocket } from 'services/signaling';
import { styleConstants } from 'theme/styleConstants';
import { useAppSelector } from 'store/hooks';
import { useVideoPip } from 'hooks/useVideoPip';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useRetryBlockedMediaOnPipGesture } from 'components/PiP/useRetryBlockedMediaOnPipGesture';
import { pipRequiresGestureTask } from 'utils/pip/pipRequiresGestureTask';
import { PipEntryPoint } from 'utils/pip/types';
import webrtcAdapter from 'webrtc-adapter';

const DEFAULT_PIP_SIZE = 500;

type PiPContextType = {
  pipWindowSupported: boolean;
  pipWindow: Window | null;
  pipVideo: HTMLVideoElement | null;
  pipWindowOpen: boolean;
  requestPipWindow: (entry: PipEntryPoint) => Promise<void>;
  closePipWindow: () => void;
};

const PiPContext = createContext<PiPContextType | undefined>(undefined);

type PiPProviderProps = {
  children: React.ReactNode;
};

export const getPiPAvailable = () => {
  if (!('documentPictureInPicture' in window)) {
    return false;
  }

  try {
    return window.self === window.top;
  } catch {
    return false;
  }
};

export const getVideoPiPAvailable = () =>
  document.createElement('video').requestPictureInPicture !== undefined;

export function PiPProvider({ children }: PiPProviderProps) {
  const isEmbedded = useAppSelector(selectIsEmbedded);

  const featureEnabled = useFeatureFlag('pictureInPicture');
  const featureAvailable = getPiPAvailable();
  const videoPiPAvailable = getVideoPiPAvailable();

  const { isMobile } = useBreakpoints();

  const pipWindowSupported =
    !isMobile && featureEnabled && (featureAvailable || (isEmbedded && videoPiPAvailable));

  // WebKit refuses a request that is not made inside the gesture task, so the element has to be
  // ready before the click
  const videoPipNeedsPreparing = pipWindowSupported && isEmbedded && pipRequiresGestureTask();

  const { requestVideoPipWindow, prepareVideoPipWindow, closeVideoPipWindow } = useVideoPip();
  const contentBoard = useContentBoard();

  const [pipWindowOpen, setPipWindowOpen] = useState<boolean>(false);
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [pipVideo, setPipVideo] = useState<HTMLVideoElement | null>(null);

  // A screenshare can request PiP after a delay, by which point the room may be gone.
  const roomLeft = useRef(false);

  useEffect(() => {
    roomLeft.current = false;

    return () => {
      roomLeft.current = true;
    };
  }, []);

  useEffect(() => {
    if (!videoPipNeedsPreparing || pipWindowOpen) {
      return;
    }

    prepareVideoPipWindow(DEFAULT_PIP_SIZE, DEFAULT_PIP_SIZE);
  }, [videoPipNeedsPreparing, pipWindowOpen, prepareVideoPipWindow]);

  // A prepared element exists before any window is open, so closePipWindow cannot reach it.
  useEffect(() => () => closeVideoPipWindow(), [closeVideoPipWindow]);

  const pipWindowRef = useRef<Window | null>(null);
  const pipVideoRef = useRef<HTMLVideoElement | null>(null);

  const closePipWindow = useCallback(() => {
    const openWindow = pipWindowRef.current;
    const openVideo = pipVideoRef.current;

    if (openWindow == null && openVideo == null) {
      return;
    }

    setPipWindowOpen(false);

    const focusBeforeClose = webrtcAdapter.browserDetails.browser === 'firefox';

    if (focusBeforeClose) {
      window.focus();
    }

    if (openWindow != null) {
      openWindow.close();
      pipWindowRef.current = null;
      setPipWindow(null);
    }

    if (openVideo != null) {
      closeVideoPipWindow();
      pipVideoRef.current = null;
      setPipVideo(null);
    }

    if (!focusBeforeClose) {
      window.focus();
    }

    SignalingSocket.send({ event: 'closePictureInPictureWindow' });
  }, [closeVideoPipWindow]);

  useEffect(() => () => closePipWindow(), [closePipWindow]);

  useRetryBlockedMediaOnPipGesture(pipWindow);

  const requestPipWindow = useCallback(
    async (entry: PipEntryPoint) => {
      if (pipWindowOpen || roomLeft.current) {
        return;
      }

      if (contentBoard.id) {
        contentBoard.hideLocalLaserPointer();
      }

      if (isEmbedded) {
        const pip = await requestVideoPipWindow(DEFAULT_PIP_SIZE, DEFAULT_PIP_SIZE, entry);

        if (!pip) {
          if (videoPipNeedsPreparing) {
            prepareVideoPipWindow(DEFAULT_PIP_SIZE, DEFAULT_PIP_SIZE);
          }

          return;
        }

        setPipWindowOpen(true);
        pipVideoRef.current = pip;
        setPipVideo(pip);

        pip.addEventListener('leavepictureinpicture', () => {
          // The event is delivered asynchronously, so by now this element may already have been
          // torn down and replaced by a freshly prepared one, which must not be destroyed.
          if (pipVideoRef.current !== pip) {
            return;
          }

          setPipWindowOpen(false);
          pipVideoRef.current = null;
          setPipVideo(null);
          closeVideoPipWindow();
        });

        SignalingSocket.send({ event: 'openPictureInPictureWindow' });

        return;
      }

      let pip: Window;

      try {
        pip = await window.documentPictureInPicture.requestWindow({
          width: DEFAULT_PIP_SIZE,
          height: DEFAULT_PIP_SIZE,
        });
      } catch (error) {
        Sentry.captureException(error, { tags: { 'pip.entry': entry } });

        return;
      }

      pip.addEventListener('pagehide', () => {
        setPipWindowOpen(false);
        pipWindowRef.current = null;
        setPipWindow(null);
        SignalingSocket.send({ event: 'closePictureInPictureWindow' });
      });

      function copyStylesToPip(pw: Window) {
        [...document.styleSheets].forEach((styleSheet) => {
          try {
            const cssRules = [...styleSheet.cssRules].map((rule) => rule.cssText).join('');
            const style = document.createElement('style');
            style.textContent = cssRules;
            pw.document.head.appendChild(style);
          } catch (e) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.type = styleSheet.type;
            // @ts-ignore
            link.media = styleSheet.media;
            // @ts-ignore
            link.href = styleSheet.href;
            // @ts-ignore
            pw.document.head.appendChild(link);
          }
        });

        const computedStyles = window.getComputedStyle(document.body);
        const bodyStyle = document.createElement('style');
        bodyStyle.textContent = `body { ${computedStyles.cssText} }`;
        pw.document.head.appendChild(bodyStyle);

        const htmlComputedStyles = window.getComputedStyle(document.documentElement);
        const htmlStyle = document.createElement('style');
        htmlStyle.textContent = `html { ${htmlComputedStyles.cssText} }`;
        pw.document.head.appendChild(htmlStyle);

        pw.document.documentElement.style.setProperty(
          '--toolbar-width',
          `${styleConstants.toolbar.width}px`
        );
      }

      const obs = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            copyStylesToPip(pip);
            break;
          }
        }
      });

      obs.observe(document.head, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      copyStylesToPip(pip);

      SignalingSocket.send({ event: 'openPictureInPictureWindow' });

      setPipWindowOpen(true);
      pipWindowRef.current = pip;
      setPipWindow(pip);
    },
    [
      pipWindowOpen,
      isEmbedded,
      videoPipNeedsPreparing,
      requestVideoPipWindow,
      prepareVideoPipWindow,
      closeVideoPipWindow,
      contentBoard,
    ]
  );

  const value = useMemo(
    () => ({
      pipWindowSupported,
      pipWindow,
      pipVideo,
      requestPipWindow,
      closePipWindow,
      pipWindowOpen,
    }),
    [closePipWindow, pipWindowSupported, pipWindow, requestPipWindow, pipWindowOpen, pipVideo]
  );

  return <PiPContext.Provider value={value}>{children}</PiPContext.Provider>;
}

export function usePiPWindow(): PiPContextType {
  const context = useContext(PiPContext);

  if (context === undefined) {
    throw new Error('usePiPWindow must be used within a PiPContext');
  }

  return context;
}
